import { arrayMove } from '@dnd-kit/sortable';
import { ColumnDef } from '@tanstack/react-table';
import { DetailedHTMLProps, memo, TableHTMLAttributes, useMemo, useState } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Menu, MenuItem, Popover, Table, Text, useComponentStack } from '@/components/core';
import { ChevronIcon, SingleValueFieldIcon, TableIcon } from '@/components/icons';
import { FieldCell, NavigationContainer, NewFieldModal } from '@/components/ui';
import { FieldState, useModelConfigStore } from '@/store';

import { FieldDetails } from './FieldDetails';

export type FieldConfigProps = DetailedHTMLProps<TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>;

const IFieldConfig = ({ className, ...rest }: FieldConfigProps) => {
  const fields = useModelConfigStore((state) => state.fields);
  const addField = useModelConfigStore((state) => state.addField);
  const setFields = useModelConfigStore((state) => state.setFields);
  const duplicateField = useModelConfigStore((state) => state.duplicateField);
  const deleteField = useModelConfigStore((state) => state.deleteField);

  const componentStack = useComponentStack();

  const columns = useMemo<ColumnDef<FieldState>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: ({ row }) => (
          <FieldCell
            field={row.original}
            onFieldItemClick={(item) =>
              componentStack?.push(
                <NavigationContainer>
                  <FieldDetails fieldId={item.id} />
                </NavigationContainer>
              )
            }
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ],
    [componentStack]
  );

  const [newFieldModalIsOpen, setNewFieldModalIsOpen] = useState(false);

  return (
    <div className="gap-md flex flex-col">
      <NewFieldModal onOpenChange={setNewFieldModalIsOpen} isOpen={newFieldModalIsOpen} />
      <MenuTrigger>
        <Button className="w-fit" variant="secondary">
          <Text>Add field</Text>
          <ChevronIcon rotation="down" />
        </Button>

        <Popover>
          <Menu>
            <MenuItem onAction={() => setNewFieldModalIsOpen(true)}>
              <SingleValueFieldIcon />
              <Text>Single value</Text>
            </MenuItem>
            <MenuItem onAction={() => addField({ type: 'table', name: 'New table' })}>
              <TableIcon />
              <Text>Table</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>

      <div className="rounded-md border-1 border-gray-100 bg-gray-50">
        <Table
          className={merge('border-separate border-spacing-x-0 border-spacing-y-2 p-4', className)}
          data={fields}
          columns={columns}
          isInnerTable={false}
          onRowIndexChange={(oldIndex, newIndex) => setFields(arrayMove(fields, oldIndex, newIndex))}
          hideHeader
          duplicateRowAction={(row) => duplicateField(row.original.id)}
          deleteRowAction={(row) => deleteField(row.original.id)}
          {...rest}
        />
      </div>
    </div>
  );
};

export const FieldConfig = memo(IFieldConfig) as typeof IFieldConfig;
